/* @jsx h */
import { defineComponent, ref, h } from "vue";
import {
  useAuth,
  isGroupSession,
  type AuthSession,
} from "@app/vue/providers/index";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { SessionSibling } from "@app/server/trpc/routers/session";
import { api } from "@app/data/api";
import { useLocalization } from "@lemmonhq/uikit";
import { RouterLink } from "vue-router";
import { RestaurantPickerModal } from "./RestaurantPickerModal";

export type LoginFormComponentProps = {
  onSuccess?: Function;
};
export const LoginFormComponent = defineComponent<LoginFormComponentProps>({
  props: ["onSuccess"],
  setup(props) {
    const email = ref("");
    const password = ref("");
    const error = ref("");
    const loading = ref(false);
    const showPassword = ref(false);

    // Set once the signed-in identity manages more than one restaurant, so the
    // manager can pick which shop to open instead of landing on the default.
    const pickerShops = ref<SessionSibling[] | null>(null);
    const switchingUserId = ref<number | null>(null);
    let pendingSession: AuthSession | null = null;

    const { setUser } = useAuth();
    const { t } = useLocalization();

    const trpc = useTrpc();

    // Commit the resolved POS session and hand off to the caller's success
    // handler. setUser also identifies the user to the native app.
    const finalizeLogin = async (userData: AuthSession) => {
      setUser(userData);
      props.onSuccess ? props.onSuccess() : null;
    };

    const handlePick = async (shop: SessionSibling) => {
      if (switchingUserId.value !== null) return;

      // Already on the chosen shop's cookie: nothing to switch, just proceed.
      if (shop.is_current && pendingSession) {
        pickerShops.value = null;
        await finalizeLogin(pendingSession);
        return;
      }

      switchingUserId.value = shop.user_id;
      try {
        await trpc.session.switchShop.mutate({ userId: shop.user_id });
        // Full reload so the new POS cookie is picked up everywhere and
        // Auth.tsx re-emits shop_id through the native bridge.
        window.location.assign("/");
      } catch (err) {
        console.error("Failed to open restaurant:", err);
        switchingUserId.value = null;
        error.value = t("auth.login.open_error");
      }
    };

    // Abandon the picker and return to a clean login form. The session cookie
    // was already set for the default shop during login, so drop it too.
    const handleCancel = async () => {
      if (switchingUserId.value !== null) return;
      pickerShops.value = null;
      pendingSession = null;
      password.value = "";
      try {
        await api.destroySession();
      } catch (err) {
        console.error("Failed to clear session on cancel:", err);
      }
    };

    const handleSubmit = async (e: Event) => {
      e.preventDefault();
      error.value = "";
      loading.value = true;
      try {
        const data = await api.createSession(email.value, password.value);
        const userData = await api.getSession();
        if (userData) {
          if (isGroupSession(userData)) {
            // A full reload makes the root provider branch before any POS-only
            // providers can mount for this identity-only session.
            window.location.assign("/group/dashboard");
            return;
          }

          // POS session: offer a restaurant picker when the identity manages
          // more than one linked shop, otherwise log straight in.
          const siblings = await loadSiblings();
          if (siblings.length > 1) {
            pendingSession = userData;
            pickerShops.value = siblings;
            return;
          }

          await finalizeLogin(userData);
        } else {
          throw new Error("failed");
        }
      } catch (err) {
        console.error("Login failed:", err);
        error.value = t("auth.login.failed");
      } finally {
        loading.value = false;
      }
    };

    // Linked restaurants the signed-in identity can open without
    // re-authenticating. Failures degrade to a normal single-shop login.
    const loadSiblings = async (): Promise<SessionSibling[]> => {
      try {
        return await trpc.session.siblings.query();
      } catch (err) {
        console.error("Failed to load linked restaurants:", err);
        return [];
      }
    };

    return () => (
      <div
        class="flex min-h-0 flex-1 items-center justify-center overflow-y-auto bg-[--body-bg-color]"
      >
        <div class=" p-8 rounded-lg  w-full max-w-md">
          <RouterLink to="/">
            <h2 class="text-6xl mb-6 text-accent text-center">Lemmon</h2>
          </RouterLink>
          {error.value && (
            <div class="bg-error/10 border border-error text-error capitalize px-4 py-2 rounded mb-4">
              {error.value}
            </div>
          )}
          <form onSubmit={handleSubmit} class="space-y-4">
            <label class="form-control w-full">
              <div class="label">
                <span class="label-text">{t("auth.login.email")}</span>
              </div>
              <input
                type="email"
                id="email"
                data-testid="input-email"
                value={email.value}
                onChange={(e) =>
                  (email.value = (e.target as HTMLInputElement).value)
                }
                class="input input-bordered w-full"
                required
              />
            </label>

            <label class="form-control w-full">
              <div class="label">
                <span class="label-text">{t("auth.login.password")}</span>
              </div>
              <div class="relative">
                <input
                  type={showPassword.value ? "text" : "password"}
                  id="password"
                  data-testid="input-password"
                  value={password.value}
                  onChange={(e) =>
                    (password.value = (e.target as HTMLInputElement).value)
                  }
                  class="input input-bordered w-full pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => (showPassword.value = !showPassword.value)}
                  class={[
                    "absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors",
                    showPassword.value && "text-active",
                  ]}
                >
                  <span class="material-symbols-outlined text-2xl">
                    {showPassword.value ? "visibility_off" : "visibility"}
                  </span>
                </button>
              </div>
            </label>

            <button
              type="submit"
              data-testid="btn-login"
              disabled={loading.value}
              class="btn btn-primary text-3xl w-full btn-md"
            >
              {loading.value ? (
                <span class="flex items-center justify-center">
                  <span class="loading loading-spinner loading mr-2"></span>
                </span>
              ) : (
                <span class="flex justify-center items-center">
                  <span class="material-symbols-outlined mr-0 text-3xl">
                    fingerprint
                  </span>{" "}
                  {t("auth.login.submit")}
                </span>
              )}
            </button>
          </form>

          <div class="py-6 text-center text-accent ">
            <RouterLink to="/login">{t("auth.login.login_with_pin")}</RouterLink>
          </div>
        </div>

        {pickerShops.value && (
          <RestaurantPickerModal
            shops={pickerShops.value}
            switchingUserId={switchingUserId.value}
            onPick={handlePick}
            onCancel={handleCancel}
          />
        )}
      </div>
    );
  },
});
